import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAllJobs } from "../../redux/slices/jobsCacheSlice";
import axiosInstance from "../../interceptors";

// ── Setup checklist (UX prompt #1, Part B) ────────────────────────────────
// Renders as a collapsible panel near the top of /admin/dashboard while at
// least one setup step is incomplete and the user hasn't dismissed.
//
// Completion detection: GET /job-posting/onboarding/setup-status returns
// {steps:{calendar, teammate, firstJob, careerPage, emailTemplates}} —
// real signal from DB rows. firstJob also falls back to the local Redux
// cache so a freshly-created job marks complete before the server
// roundtrip completes.
//
// Persistence:
//   - "Dismiss for now"     → hidden for 7 days (localStorage timestamp)
//   - "Don't show again"    → hidden permanently for this user/browser

const DISMISS_KEY = "ample_setup_checklist_dismissed_until";
const PERMANENT_KEY = "ample_setup_checklist_permanently_hidden";
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const STEPS_BASE = [
  {
    id: "calendar",
    label: "Connect your calendar",
    description: "Google or Outlook so interviews land on your schedule.",
    cta: "Connect",
    to: "/admin/scheduling",
  },
  {
    id: "teammate",
    label: "Invite a teammate",
    description: "Hiring is a team sport — invite a colleague to collaborate.",
    cta: "Invite",
    to: "/admin/team-members",
  },
  {
    id: "firstJob",
    label: "Create your first job",
    description: "Define the role, requirements, and screening flow.",
    cta: "Create",
    to: "/admin/jobs",
  },
  {
    id: "careerPage",
    label: "Set up your career page",
    description: "A branded landing page at /careers/{your-slug} for inbound candidates.",
    cta: "Set up",
    to: "/admin/career-pages/new",
  },
  {
    id: "emailTemplates",
    label: "Customize your email templates",
    description: "Personalize the candidate-facing emails — invites, reminders, decisions.",
    cta: "Customize",
    to: "/admin/email-templates",
  },
];

function readDismissedUntil() {
  try {
    const v = Number(localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(v) && v > Date.now() ? v : 0;
  } catch { return 0; }
}

function readPermanentlyHidden() {
  try { return localStorage.getItem(PERMANENT_KEY) === "true"; }
  catch { return false; }
}

export default function SetupChecklist() {
  const navigate = useNavigate();
  const jobs = useSelector(selectAllJobs);

  // Hide if user dismissed (7-day) or said "don't show again". Re-evaluates
  // on mount; consumers can also force-show by clearing the keys.
  const [dismissedUntil, setDismissedUntil] = useState(readDismissedUntil);
  const [permanentlyHidden, setPermanentlyHidden] = useState(readPermanentlyHidden);
  const [collapsed, setCollapsed] = useState(false);
  const [serverSteps, setServerSteps] = useState(null);

  useEffect(() => {
    // If the 7-day window has elapsed, drop the timestamp.
    if (dismissedUntil > 0 && dismissedUntil <= Date.now()) {
      try { localStorage.removeItem(DISMISS_KEY); } catch {}
      setDismissedUntil(0);
    }
  }, [dismissedUntil]);

  // Pull real completion state from the backend. Failure is silent —
  // the checklist falls back to the local-cache-only signal (firstJob
  // from Redux). Users won't lose progress, just won't see the
  // server-only steps tick over.
  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("/job-posting/onboarding/setup-status")
      .then((r) => {
        if (cancelled) return;
        if (r?.data?.success && r.data.steps) setServerSteps(r.data.steps);
      })
      .catch(() => { /* fall back to local-only signal */ });
    return () => { cancelled = true; };
  }, []);

  const completion = useMemo(() => {
    // firstJob: prefer the local Redux cache (instant after a new
    // job-create) and OR-fold the server's signal.
    const localFirstJob = Array.isArray(jobs) && jobs.length > 0;
    const s = serverSteps || {};
    return {
      calendar: !!s.calendar,
      teammate: !!s.teammate,
      firstJob: localFirstJob || !!s.firstJob,
      careerPage: !!s.careerPage,
      emailTemplates: !!s.emailTemplates,
    };
  }, [jobs, serverSteps]);

  const completed = Object.values(completion).filter(Boolean).length;
  const total = STEPS_BASE.length;
  const pct = Math.round((completed / total) * 100);
  const allDone = completed === total;

  if (permanentlyHidden || dismissedUntil > 0 || allDone) return null;

  const dismiss = () => {
    const until = Date.now() + SEVEN_DAYS_MS;
    try { localStorage.setItem(DISMISS_KEY, String(until)); } catch {}
    setDismissedUntil(until);
  };
  const hideForever = () => {
    try { localStorage.setItem(PERMANENT_KEY, "true"); } catch {}
    setPermanentlyHidden(true);
  };

  return (
    <section
      aria-labelledby="setup-checklist-title"
      data-tour="setup-checklist"
      className="bg-white border border-subtle rounded-card overflow-hidden mb-4"
    >
      <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-subtle bg-vf-tint/30">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-8 h-8 rounded-full bg-vf-primary text-white flex items-center justify-center flex-shrink-0 text-xs font-bold"
            aria-hidden="true"
          >
            {completed}/{total}
          </div>
          <div className="min-w-0">
            <h2 id="setup-checklist-title" className="text-sm font-semibold text-vf-base m-0">
              Set up Ample Hire
            </h2>
            <p className="text-xs text-vf-muted mt-0.5">
              {completed} of {total} steps complete — finish a few to make this workspace yours.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls="setup-checklist-body"
          className="text-xs text-vf-muted hover:text-vf-base px-2 py-1 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
        >
          {collapsed ? "Show" : "Hide"}
        </button>
      </header>

      {/* Progress bar */}
      <div
        className="h-1 bg-vf-tint relative"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Setup progress"
      >
        <div
          className="h-full bg-vf-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      {!collapsed && (
        <div id="setup-checklist-body">
          <ol className="list-none p-0 m-0">
            {STEPS_BASE.map((s, i) => {
              const done = completion[s.id];
              return (
                <li
                  key={s.id}
                  className={`flex items-start gap-3 px-5 py-3 ${i > 0 ? "border-t border-subtle" : ""}`}
                >
                  <span
                    className={`flex-shrink-0 w-5 h-5 rounded-full border flex items-center justify-center mt-0.5 ${
                      done
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "bg-white border-slate-300"
                    }`}
                    aria-hidden="true"
                  >
                    {done && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${done ? "text-vf-muted line-through" : "text-vf-base"}`}>
                      {s.label}
                      <span className="sr-only">{done ? " — complete" : " — not complete"}</span>
                    </div>
                    {!done && <div className="text-xs text-vf-muted mt-0.5">{s.description}</div>}
                  </div>
                  {!done && (
                    <button
                      type="button"
                      onClick={() => navigate(s.to)}
                      className="text-xs font-semibold text-vf-primary hover:underline px-2 py-1 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
                    >
                      {s.cta} →
                    </button>
                  )}
                </li>
              );
            })}
          </ol>

          <footer className="flex items-center justify-end gap-3 px-5 py-2.5 border-t border-subtle bg-vf-bg">
            <button
              type="button"
              onClick={dismiss}
              className="text-xs text-vf-muted hover:text-vf-base"
            >
              Dismiss for now
            </button>
            <span className="text-vf-muted" aria-hidden="true">·</span>
            <button
              type="button"
              onClick={hideForever}
              className="text-xs text-vf-muted hover:text-red-600"
            >
              Don't show again
            </button>
          </footer>
        </div>
      )}
    </section>
  );
}
